import { Api } from './api'
import { getClientLanguage } from './locales';
import { getNotifiedFor as _getNotifiedFor, changeNotificationCustomerFilter as _changeNotificationCustomerFilter } from './notification';
import { getOnboardingStatus as _getOnboardingStatus } from './onboarding';
import type { Customer, ConversationEvent } from '../types/conversation';
import type { Order } from '../types/order';
import type { Account } from '../types/account';
import type {
  ErrorResponse, User, ListUsersResponse, CreateUserResponse, PatchUserResponse,
  AccountOrderCountResponse, ExampleCustomersResponse, BadgeNumbersResponse,
  ListFlagsResponse, Workflow, ListWorkflowsResponse,
  Integration, ListIntegrationsResponse, WhatsappRegisterResponse,
  FaqItem, ListFaqItemsResponse, GetFeedbackResponse,
  BillingInfo, InsightsResponse, AutomationInsightsResponse, SmsResponse,
  WebsiteScan, ChatSettings, ListPlansResponse, ListConversationEventsResponse,
  ListCustomerOrdersResponse, ListCustomerJourneyLogsResponse,
} from '../types/api';

export const api = new Api({
  baseURL: process.env.TALKVISOR_API_URL,
  withCredentials: true,
})

export async function listPlans(): Promise<ListPlansResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/plans`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function getMe(): Promise<User | ErrorResponse> { return getUser('me') }

export async function listUsers(): Promise<ListUsersResponse | ErrorResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function getUser(userId: string): Promise<User> {
  const res = await api.v1.getUser(userId)
  const user = res.data
  if (user.id === undefined) {
    user.id = ''
  }
  return user as User
}

export async function getUserByEmail(email: string): Promise<User> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users/by-email/${email}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export function getUserThumbnail(userId: string): string {
  return `${process.env.TALKVISOR_API_URL}/v1/users/${userId}/thumbnail`
}

export function getShopifyProductThumbnail(accountId: string, productId: string, variantId: string): string {
  return `${process.env.TALKVISOR_API_URL}/v1/products/${accountId}/shopify/${productId}/${variantId}/thumbnail`
}

export async function updateUser(userId: string, attributes: Record<string, any>): Promise<PatchUserResponse | ErrorResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users/${userId}`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify(attributes),
    }
  )
  return res.json()
}

export async function updateUserThumbnail(userId: string, file: File): Promise<PatchUserResponse | ErrorResponse> {
  const formData = new FormData()
  formData.append("file", file)

  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users/${userId}/thumbnail`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: formData,
    }
  )
  return res.json()
}

export function getAccountLogo(accountId: string): string {
  return `${process.env.TALKVISOR_API_URL}/v1/accounts/${accountId}/logo`
}

export async function updateAccountLogo(accountId: string, file: File): Promise<Response> {
  const formData = new FormData()
  formData.append("file", file)

  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/${accountId}/logo`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: formData,
    }
  )
  return res
}

export async function updateWidgetIcon(accountId: string, file: File): Promise<Account> {
  const formData = new FormData()
  formData.append("file", file)

  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/${accountId}/widget_icon`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: formData,
    }
  )
  return res.json()
}

export async function deleteWidgetIcon(accountId: string): Promise<Account> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/${accountId}/widget_icon`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  )
  return res.json()
}

export function getWidgetIcon(accountId: string): string {
  return `${process.env.TALKVISOR_API_URL}/v1/accounts/${accountId}/widget_icon`
}

export async function getOrderCount(): Promise<AccountOrderCountResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/order-count`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function createUser(user: Record<string, any>): Promise<CreateUserResponse | ErrorResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(user),
    }
  )
  return res.json()
}

export async function deleteUser(userId: string): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users/${userId}`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function getAccount(accountId: string): Promise<Account | ErrorResponse> {
  const res = await api.v1.getAccount(accountId)
  const account = res.data;
  if (account.id === undefined) {
    account.id = '';
  }
  if (account.hours === undefined) {
    account.hours = {};
  }
  return account as Account;
}

export async function getAccountMe(): Promise<Account | ErrorResponse> { return getAccount('me') }

export async function getExampleCustomers(): Promise<ExampleCustomersResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/example_customers`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function refreshExampleCustomers(): Promise<ExampleCustomersResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/example_customers`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function updateAccount(accountId: string, attributes: Record<string, any>): Promise<Account | ErrorResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/${accountId}`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify(attributes),
    }
  )
  return res.json()
}

export async function getBadgeNumbers(): Promise<BadgeNumbersResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/badges`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function listFlags(): Promise<ListFlagsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/flags`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function listConversationEvents(conversationId: string): Promise<ListConversationEventsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/conversations/${conversationId}/conversation_events`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function getConversation(conversationId: string): Promise<Customer> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/conversations/${conversationId}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function createConversation(customerId: string, subject: string, workflowId: string): Promise<{ id: string }> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/conversations`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        subject,
        customer_id: customerId,
        workflow_id: workflowId
      }),
    }
  )
  return res.json()
}

export async function getCustomer(customerId: string): Promise<Customer> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export type ListCustomersResponse = {
  customers: Customer[];
  total: number;
  data?: {
    orders?: Order[];
  }
  error?: string;
};

export async function listCustomers({
  conversationStatus = undefined,
  offset = undefined,
  limit = undefined,
  order = undefined,
  query = undefined,
  workflow = undefined,
  workflowNot = undefined,
  customerJourneyAfter = undefined,
  customerJourney = undefined,
  livechat = undefined,
  showHidden = undefined
}: {
  conversationStatus?: string;
  offset?: number;
  limit?: number;
  order?: string;
  query?: string;
  workflow?: string;
  workflowNot?: string;
  customerJourneyAfter?: string;
  customerJourney?: string;
  livechat?: boolean;
  showHidden?: boolean;
}): Promise<ListCustomersResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers?offset=${offset || ''}&limit=${limit || ''}&order=${order || ''}&query=${query || ''}&conversation_status=${conversationStatus || ''}&workflow=${workflow || ''}&workflow_not=${workflowNot || ''}&customer_journey=${customerJourney || ''}&customer_journey_after=${customerJourneyAfter || ''}&livechat=${livechat || ''}&show_hidden=${showHidden || ''}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function updateCustomer(customerId: string, attributes: Record<string, any>): Promise<any> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify(attributes),
    }
  )
  return res.json()
}

export async function listCustomerOrders(customerId: string): Promise<ListCustomerOrdersResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}/orders`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function listCustomerJourneyLogs(customerId: string, offset?: number, limit?: number, signal?: AbortSignal): Promise<ListCustomerJourneyLogsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}/journey_logs?offset=${offset || ''}&limit=${limit || ''}`,
    {
      method: 'GET',
      credentials: 'include',
      signal, // Add the AbortSignal here
    }
  )
  return res.json()
}

export async function getConversationEvent(customerId: string): Promise<ListConversationEventsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}/conversation_events`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function createConversationEvent(customerId: string, type: string, text: string, actions: Record<string, any>[]): Promise<ConversationEvent> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}/conversation_events`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ type, payload: { text, actions } }),
    }
  )
  return res.json()
}

export async function createAgentTyping(customerId: string, show: string): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}/conversation_events/agent_typing`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ show }),
    }
  )
  return res.json()
}

export function getConversationEventAttachmentURL(attachmentUrl: string): string {
  const pathArr = attachmentUrl.split("/");
  const conversationEventId = pathArr[pathArr.length - 1];

  const apiURL = process.env.TALKVISOR_API_URL

  return `${apiURL}/v1/conversation_events/${conversationEventId}/attachment`;
}

export async function getWorkflow(id: string): Promise<Workflow> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/workflows/${id}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function listWorkflows(): Promise<ListWorkflowsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/workflows`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function listIntegrations(): Promise<ListIntegrationsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/integrations`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function createIntegration(
  type: string,
  accessToken: string | null = null,
  handle: string = ""
): Promise<Integration> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/integrations`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        type,
        access_token: accessToken || "-",
        refresh_token: "-",
        expiry: "1970-01-01T00:00:00Z",
        handle: handle,
      }),
    }
  )
  return res.json()
}

export async function deleteIntegration(integrationId: string): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/integrations/${integrationId}`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function whatsappRegister(integrationId: string, pin: string): Promise<WhatsappRegisterResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/integrations/${integrationId}/whatsapp_register`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        pin,
      }),
    }
  )
  return res.json()
}

export async function switchAccount(accountId: string): Promise<{ user_id: string; account_id: string }> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/${accountId}/switch`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function signIn(myshopify_domain: string, email: string, password: string): Promise<{ user_id: string; account_id: string }> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users/signin`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        myshopify_domain,
        email,
        password,
      }),
    }
  )
  return res.json()
}

export async function initiateResetPassword(email: string): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users/reset_password`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ email }),
    }
  )
  return res.json()
}

export async function resetPassword(userId: string, token: string, password: string): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/users/${userId}/reset_password`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        token,
        password
      }),
    }
  )
  return res.json()
}

export async function getSMS(): Promise<SmsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/notification_destinations/sms`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function postSMS(phone_number: string, verification_code: string): Promise<SmsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/notification_destinations/sms`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ phone_number, verification_code }),
    }
  )
  return res.json()
}

export async function updateConversation(customerId: string, attributes: Record<string, any>): Promise<Customer> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify(attributes),
    }
  )
  return res.json()
}

export async function getFeedback(orderId: string): Promise<GetFeedbackResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/feedbacks/${orderId}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function patchFeedback(productFeedbacks: Array<any>, overallFeedback: any): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/feedbacks`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify({
        product_feedbacks: productFeedbacks,
        overall_feedback: overallFeedback,
      }),
    }
  )
  return res.json()
}

export async function getInsights(): Promise<InsightsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/insights`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function getAutomationInsights(): Promise<AutomationInsightsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/insights/automation`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function getBilling(): Promise<BillingInfo> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/billing`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function enableBilling(planCode: string): Promise<BillingInfo> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/billing`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        plan_code: planCode,
      }),
    }
  )
  return res.json()
}

export async function updateOrder(customerId: string, order: { id: string, [key: string]: any }): Promise<Order> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/customers/${customerId}/orders/${order.id}`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify(order),
    }
  )
  return res.json()
}

function sleep(duration: number): Promise<void> {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, duration);
  });
}

export function getQueryStringByName(name: string, url: string = window.location.href): string | null {
  name = name.replace(/[\[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function getMyshopifyHost(): string {
  return window.Shopify?.shop || location.host;
}

export async function getCart(): Promise<{ token: string } | string> {
  const token = window.Shopify.tv_account_id;
  if (token) {
    return { token };
  }

  try {
    return await fetch('/cart.js').then(x => x.json());
  } catch (e) {
    let uniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    return 'nocart-' + uniqueId;
  }
}

export async function getChatSettings(cartJs: { token: string }, additionalQuery?: string): Promise<ChatSettings> {
  const apiURL = process.env.TALKVISOR_API_URL;
  const customerId = getQueryStringByName('customer_id');
  const language = getClientLanguage();
  const res = await fetch(
    `${apiURL}/w/v1/accounts/${getMyshopifyHost()}?${additionalQuery || ''}&lang=${language}&customer_id=${customerId || ''}&shopify_cart_id=${cartJs.token}&referrer=${document.referrer || ''}`,
    {
      credentials: 'include',
    }
  );
  const resJson = await res.json();

  if (!resJson.error) {
    return resJson;
  } else {
    console.error(resJson.error);
    // Retry
    await sleep(1000);
    return getChatSettings(cartJs, additionalQuery);
  }
}

export function getThumbnailURL(userId: string): string {
  const apiURL = process.env.TALKVISOR_API_URL;
  return `${apiURL}/v1/users/${userId}/thumbnail`;
}

export function getLoadingGifURL(): string {
  const apiURL = process.env.TALKVISOR_API_URL;
  return `${apiURL}/v1/users/loading_image`;
}

export async function sendAutoForwardingEmail(forwardEmail: string): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/accounts/notify/auto_forwarding`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        email: forwardEmail,
      }),
    }
  )
  return res.json()
}

export async function postWebsiteScan(url: string, email: string): Promise<{ id: string }> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/website_scans`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        url,
        email,
      }),
    }
  )
  return res.json()
}

export async function getWebsiteScan(id: string): Promise<WebsiteScan> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/website_scans/${id}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function fetchShopifyProductData(productUrl: string): Promise<any> {
  const url = new URL(productUrl);
  const cleanUrl = `${url.origin}${url.pathname}.json`;

  const res = await fetch(cleanUrl, {
    method: "GET",
  });
  return res.json();
}

export async function listFaqItems(): Promise<ListFaqItemsResponse> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/faq_items`,
    {
      method: 'GET',
      credentials: 'include',
    }
  )
  return res.json()
}

export async function createFaqItem(faqItem: { question: string, answer: string }): Promise<FaqItem> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/faq_items`,
    {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(faqItem),
    }
  )
  return res.json()
}


export async function updateFaqItem(faqItemId: string, attributes: { question: string, answer: string }): Promise<FaqItem> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/faq_items/${faqItemId}`,
    {
      method: 'PATCH',
      credentials: 'include',
      body: JSON.stringify(attributes),
    }
  )
  return res.json()
}

export async function deleteFaqItem(faqItemId: string): Promise<void> {
  const apiURL = process.env.TALKVISOR_API_URL
  const res = await fetch(
    `${apiURL}/v1/faq_items/${faqItemId}`,
    {
      method: 'DELETE',
      credentials: 'include',
    }
  )
  return res.json()
}

export const getNotifiedFor = _getNotifiedFor;
export const changeNotificationCustomerFilter = _changeNotificationCustomerFilter;
export const getOnboardingStatus = _getOnboardingStatus;
